import React from "react";
import { Link } from "react-router-dom";
import { MarketingLayout } from "./MarketingLayout";
import { MarketingCTA } from "./MarketingCTA";
import { FaqAccordion } from "./FaqAccordion";
import { SEO } from "../seo/SEO";
import { breadcrumbSchema, faqSchema } from "../seo/schema";
import { getMarketingIcon } from "../../data/marketingIcons";
import { FEATURES_BY_SLUG } from "../../data/featuresData";
import { INDUSTRIES_BY_SLUG } from "../../data/industriesData";

/** Reusable /solutions/:slug page template — problem/solution framing (distinct from /features docs framing). */
export function SolutionTemplate({ data }) {
  const Icon = getMarketingIcon(data.icon);
  const breadcrumb = [{ name: "Home", path: "/" }, { name: "Solutions", path: "/solutions" }, { name: data.title, path: `/solutions/${data.slug}` }];

  return (
    <MarketingLayout breadcrumb={breadcrumb}>
      <SEO
        title={data.metaTitle}
        description={data.metaDescription}
        path={`/solutions/${data.slug}`}
        jsonLd={[breadcrumbSchema(breadcrumb), data.faqs?.length ? faqSchema(data.faqs) : null]}
      />

      <section className="relative overflow-hidden">
        <div className="absolute top-[-15%] left-1/2 -translate-x-1/2 h-72 w-[42rem] bg-emerald-500/10 blur-[130px] rounded-full pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-5 lg:px-8 pt-10 pb-14 text-center">
          <div className="h-14 w-14 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto mb-6">
            <Icon className="h-7 w-7 text-emerald-400" />
          </div>
          {data.painPoint && (
            <p className="text-emerald-400 font-bold text-[13px] tracking-wide uppercase mb-3">{data.painPoint}</p>
          )}
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight leading-[1.05] mb-5" data-testid="solution-h1">
            {data.title}
          </h1>
          <p className="text-[15.5px] text-slate-400 leading-relaxed max-w-2xl mx-auto">{data.intro}</p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 lg:px-8 pb-16">
        <h2 className="text-2xl font-bold text-white text-center mb-8">How ServiceGuru Pro solves it</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {data.benefits.map((b) => (
            <div key={b.title} className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid={`solution-benefit-${b.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <h3 className="text-white font-semibold text-[15px] mb-1.5">{b.title}</h3>
              <p className="text-[13.5px] text-slate-400 leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {data.faqs?.length > 0 && (
        <section className="max-w-3xl mx-auto px-5 lg:px-8 pb-16">
          <h2 className="text-2xl font-bold text-white text-center mb-8">Frequently asked questions</h2>
          <FaqAccordion faqs={data.faqs} />
        </section>
      )}

      <section className="max-w-5xl mx-auto px-5 lg:px-8 pb-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
          {data.relatedFeatures?.length > 0 && (
            <div>
              <h3 className="text-[12px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Features included in this solution</h3>
              <div className="flex flex-wrap gap-2">
                {data.relatedFeatures.map((slug) => {
                  const rf = FEATURES_BY_SLUG[slug];
                  if (!rf) return null;
                  return (
                    <Link key={slug} to={`/features/${slug}`} className="px-3 py-1.5 rounded-full border border-white/10 text-[12.5px] text-slate-300 hover:text-white hover:border-emerald-400 transition-colors">
                      {rf.title}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
          {data.relatedIndustries?.length > 0 && (
            <div>
              <h3 className="text-[12px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Popular with these industries</h3>
              <div className="flex flex-wrap gap-2">
                {data.relatedIndustries.map((slug) => {
                  const ri = INDUSTRIES_BY_SLUG[slug];
                  if (!ri) return null;
                  return (
                    <Link key={slug} to={`/industries/${slug}`} className="px-3 py-1.5 rounded-full border border-white/10 text-[12.5px] text-slate-300 hover:text-white hover:border-emerald-400 transition-colors">
                      {ri.title}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      <MarketingCTA heading="See it working in your business" sub="Start your 14-day free trial — no credit card required." />
    </MarketingLayout>
  );
}
